import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useGameStore } from '../store/gameStore';

interface CompanionStatsModalProps {
  isOpen: boolean;
  onClose: () => void;
  companion: any;
  slotNumber: number;
}

const CompanionStatsModal: React.FC<CompanionStatsModalProps> = ({ 
  isOpen, 
  onClose, 
  companion, 
  slotNumber 
}) => {
  const { formatNumber, getCompanionBonus } = useGameStore();

  if (!companion) return null;

  const getTypeColor = (type: string) => {
    switch (type) {
      case 'legendary': return 'from-yellow-400 to-orange-500';
      case 'epic': return 'from-purple-400 to-pink-500';
      case 'rare': return 'from-blue-400 to-cyan-500';
      default: return 'from-gray-400 to-gray-500';
    }
  };

  const getTypeLabel = (type: string) => {
    switch (type) {
      case 'legendary': return 'Legendär';
      case 'epic': return 'Episch';
      case 'rare': return 'Selten';
      default: return 'Gewöhnlich';
    }
  };

  const getAbilityIcon = (type: string) => {
    switch (type) {
      case 'clickMultiplier': return '👆';
      case 'coinPerSecond': return '💰';
      case 'gemChance': return '💎';
      case 'xpBonus': return '⭐';
      case 'gardenBonus': return '🌺';
      case 'autoClickPower': return '🤖';
      case 'upgradeDiscount': return '🏷️';
      case 'prestigeBonus': return '🌟';
      case 'criticalHit': return '💥';
      case 'healing': return '💚';
      case 'shield': return '🛡️';
      case 'counterAttack': return '🔄';
      case 'lifeSteal': return '🩸';
      case 'armorPenetration': return '🗡️';
      default: return '✨';
    }
  };

  const getAbilityLabel = (type: string) => {
    switch (type) {
      case 'clickMultiplier': return 'Klick-Multiplikator';
      case 'coinPerSecond': return 'Münzen pro Sekunde';
      case 'gemChance': return 'Edelstein-Chance';
      case 'xpBonus': return 'Erfahrungs-Bonus';
      case 'gardenBonus': return 'Garten-Bonus';
      case 'autoClickPower': return 'Auto-Klick-Kraft';
      case 'upgradeDiscount': return 'Upgrade-Rabatt';
      case 'prestigeBonus': return 'Prestige-Bonus';
      case 'criticalHit': return 'Kritische Treffer';
      case 'healing': return 'Heilung';
      case 'shield': return 'Schild';
      case 'counterAttack': return 'Gegenangriff';
      case 'lifeSteal': return 'Lebensentzug';
      case 'armorPenetration': return 'Rüstungsdurchdringung';
      default: return type;
    }
  };

  const getAbilitySuffix = (type: string) => {
    switch (type) {
      case 'clickMultiplier':
      case 'gemChance':
      case 'xpBonus':
      case 'gardenBonus':
      case 'upgradeDiscount':
      case 'prestigeBonus':
      case 'lifeSteal':
        return '%';
      default:
        return '';
    }
  };

  const isCombatAbility = (type: string) => {
    return ['criticalHit', 'healing', 'shield', 'counterAttack', 'lifeSteal', 'armorPenetration'].includes(type);
  };

  const standardAbilities = companion.abilities.filter((ability: any) => !isCombatAbility(ability.type));
  const combatAbilities = companion.abilities.filter((ability: any) => isCombatAbility(ability.type));

  return (
    <AnimatePresence key="companion-stats-modal">
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4 modal-container"
          onClick={onClose}
        >
          <motion.div
            initial={{ scale: 0.9, opacity: 0, y: 20 }}
            animate={{ scale: 1, opacity: 1, y: 0 }}
            exit={{ scale: 0.9, opacity: 0, y: 20 }}
            className="bg-white rounded-xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto modal-content"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className={`sticky top-0 bg-gradient-to-r ${getTypeColor(companion.type)} text-white p-4 sm:p-6 rounded-t-xl`}>
              <div className="flex justify-between items-start">
                <div className="flex items-center gap-4">
                  <div className="text-4xl sm:text-5xl">
                    {companion.icon.startsWith('data:') ? (
                      <img 
                        src={companion.icon} 
                        alt={companion.name}
                        className="w-12 h-12 sm:w-16 sm:h-16 object-contain"
                        style={{ display: 'inline-block' }}
                      />
                    ) : (
                      companion.icon
                    )}
                  </div>
                  <div>
                    <h1 className="text-xl sm:text-2xl font-bold">{companion.name}</h1>
                    <div className="flex items-center gap-2 mt-1">
                      <span className="text-sm bg-white/20 px-2 py-1 rounded-full">
                        {getTypeLabel(companion.type)}
                      </span>
                      <span className="text-sm bg-white/20 px-2 py-1 rounded-full">
                        Slot #{slotNumber}
                      </span>
                    </div>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="text-white hover:text-gray-200 transition-colors duration-200 text-2xl sm:text-3xl font-bold min-w-[44px] min-h-[44px] flex items-center justify-center touch-manipulation"
                >
                  ×
                </button>
              </div>
            </div>

            {/* Content */}
            <div className="p-4 sm:p-6 space-y-6">
              {/* Beschreibung */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-bold text-gray-800 mb-2">📖 Beschreibung</h3>
                <p className="text-gray-700 text-sm leading-relaxed">{companion.description}</p>
              </div>

              {/* Standard-Fähigkeiten */}
              {standardAbilities.length > 0 && (
                <div className="bg-blue-50 rounded-lg p-4">
                  <h3 className="font-bold text-blue-800 mb-3 flex items-center gap-2">
                    🎯 Standard-Fähigkeiten
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {standardAbilities.map((ability: any, index: number) => (
                      <div key={index} className="bg-white rounded-lg p-3 border border-blue-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{getAbilityIcon(ability.type)}</span>
                            <span className="font-semibold text-blue-700 text-sm">
                              {getAbilityLabel(ability.type)}
                            </span>
                          </div>
                        </div>
                        <div className="text-2xl font-bold text-blue-600">
                          +{formatNumber(ability.value)}{getAbilitySuffix(ability.type)}
                        </div>
                        {ability.enhanced && (
                          <div className="text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded mt-1">
                            ⚔️ Verstärkt durch Kampf-Level
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Kampf-Fähigkeiten */}
              {combatAbilities.length > 0 && (
                <div className="bg-red-50 rounded-lg p-4">
                  <h3 className="font-bold text-red-800 mb-3 flex items-center gap-2">
                    ⚔️ Kampf-Fähigkeiten
                  </h3>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    {combatAbilities.map((ability: any, index: number) => (
                      <div key={index} className="bg-white rounded-lg p-3 border border-red-200">
                        <div className="flex items-center justify-between mb-2">
                          <div className="flex items-center gap-2">
                            <span className="text-lg">{getAbilityIcon(ability.type)}</span>
                            <span className="font-semibold text-red-700 text-sm">
                              {getAbilityLabel(ability.type)}
                            </span>
                          </div>
                        </div>
                        <div className="text-2xl font-bold text-red-600">
                          +{formatNumber(ability.value)}{getAbilitySuffix(ability.type)}
                        </div>
                        {ability.enhanced && (
                          <div className="text-xs text-yellow-600 bg-yellow-50 px-2 py-1 rounded mt-1">
                            ⚔️ Verstärkt durch Kampf-Level
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Kampf-Statistiken */}
              {companion.combatStats && (
                <div className="bg-purple-50 rounded-lg p-4">
                  <h3 className="font-bold text-purple-800 mb-3 flex items-center gap-2">
                    🏆 Kampf-Statistiken
                  </h3>
                  <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                    <div className="bg-white rounded-lg p-3 text-center border border-purple-200">
                      <div className="text-2xl font-bold text-purple-600">{companion.combatStats.level || 1}</div>
                      <div className="text-xs text-purple-700">Kampf-Level</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-purple-200">
                      <div className="text-2xl font-bold text-purple-600">{companion.combatStats.experience || 0}</div>
                      <div className="text-xs text-purple-700">Kampf-Erfahrung</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-purple-200">
                      <div className="text-2xl font-bold text-purple-600">{companion.combatStats.wins || 0}</div>
                      <div className="text-xs text-purple-700">Siege</div>
                    </div>
                    <div className="bg-white rounded-lg p-3 text-center border border-purple-200">
                      <div className="text-2xl font-bold text-purple-600">{companion.combatStats.losses || 0}</div>
                      <div className="text-xs text-purple-700">Niederlagen</div>
                    </div>
                  </div>
                </div>
              )}

              {/* Entdeckungs-Info */}
              <div className="bg-gray-50 rounded-lg p-4">
                <h3 className="font-bold text-gray-800 mb-2">📅 Entdeckt</h3>
                <p className="text-gray-700 text-sm">
                  {new Date(companion.discoveredAt).toLocaleDateString('de-DE', {
                    year: 'numeric',
                    month: 'long',
                    day: 'numeric',
                    hour: '2-digit',
                    minute: '2-digit'
                  })}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-4 rounded-b-xl border-t border-gray-200">
              <div className="text-center">
                <button
                  onClick={onClose}
                  className="bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 text-white px-6 py-2 rounded-lg font-medium transition-all duration-200 transform hover:scale-105 touch-manipulation"
                >
                  Schließen
                </button>
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default CompanionStatsModal; 